import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Shield, ClipboardCheck, Calculator, Heart, Share2, MapPin, Calendar, Gauge, Fuel, Settings, Car, CheckCircle, AlertTriangle } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CarDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const car = location.state?.car;
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Track view
    if (car) {
      axios.post(`${API}/track/click`, {
        action_type: 'car_detail_view',
        car_id: car.id,
        source: car.source
      }).catch(e => console.error('Track failed:', e));
    }
  }, [car]);

  if (!car) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">No vehicle selected</h2>
          <p className="text-gray-500 mb-4">Please search for a vehicle first</p>
          <button onClick={() => navigate('/')} className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold">
            Search Cars
          </button>
        </div>
      </div>
    );
  }

  // Calculate finance estimate (prime + 2%, 72 months, 10% deposit)
  const calculateFinance = (price) => {
    if (!price) return null;
    const deposit = price * 0.1;
    const principal = price - deposit;
    const rate = 0.1175 / 12; // 11.75% annual = prime + 2%
    const months = 72;
    const payment = principal * (rate * Math.pow(1 + rate, months)) / (Math.pow(1 + rate, months) - 1);
    return Math.round(payment);
  };

  // Calculate insurance estimate
  const calculateInsurance = (price, year) => {
    if (!price) return null;
    const age = new Date().getFullYear() - (year || 2020);
    const baseRate = 0.025; // 2.5% of value annually
    const ageFactor = age > 5 ? 1.2 : age > 3 ? 1.1 : 1;
    const monthlyPremium = (price * baseRate * ageFactor) / 12;
    return Math.round(monthlyPremium);
  };

  const monthlyFinance = calculateFinance(car.price);
  const monthlyInsurance = calculateInsurance(car.price, car.year);

  const handleViewOriginal = async () => {
    await axios.post(`${API}/track/click`, {
      action_type: 'view_original',
      car_id: car.id,
      source: car.source,
      referral_url: car.source_url
    }).catch(e => console.error('Track failed:', e));
    window.open(car.source_url, '_blank');
  };

  const handleInspection = async () => {
    await axios.post(`${API}/track/click`, {
      action_type: 'inspection_click',
      car_id: car.id,
      source: car.source
    }).catch(e => console.error('Track failed:', e));
    navigate('/inspector', { state: { car } });
  };

  const handleInsurance = async () => {
    await axios.post(`${API}/track/click`, {
      action_type: 'insurance_click',
      car_id: car.id,
      source: car.source
    }).catch(e => console.error('Track failed:', e));
    navigate('/insurance', { state: { car } });
  };

  const handleFinance = async () => {
    await axios.post(`${API}/track/click`, {
      action_type: 'finance_click',
      car_id: car.id,
      source: car.source
    }).catch(e => console.error('Track failed:', e));
    navigate('/finance', { state: { car } });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
            Back to results
          </button>
        </div>
      </div>

      <div className="container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm mb-6">
              <div className="aspect-video bg-gray-100 relative">
                {car.image_url ? (
                  <img
                    src={car.image_url}
                    alt={car.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                ) : null}
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200" style={{zIndex: car.image_url ? -1 : 1}}>
                  <Car className="w-24 h-24 text-gray-300" />
                </div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-black/70 text-white text-sm rounded-full font-medium">
                    {car.source}
                  </span>
                </div>
                <div className="absolute top-4 right-4 flex gap-2">
                  <button
                    onClick={() => setSaved(!saved)}
                    className={`p-2 rounded-full ${saved ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-600'}`}
                  >
                    <Heart className={`w-5 h-5 ${saved ? 'fill-current' : ''}`} />
                  </button>
                  <button className="p-2 rounded-full bg-white/90 text-gray-600">
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Title & Price */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{car.title}</h1>
                  <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{car.year || 'N/A'}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1"><Gauge className="w-4 h-4" />{car.mileage ? `${car.mileage.toLocaleString()} km` : 'N/A'}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{car.location || 'National'}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-600">
                    {car.price ? `R${car.price.toLocaleString()}` : 'Price on request'}
                  </div>
                  {monthlyFinance && (
                    <div className="text-sm text-gray-500">Est. R{monthlyFinance.toLocaleString()}/month</div>
                  )}
                </div>
              </div>
            </div>

            {/* Specifications */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Specifications</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-500 mb-1">Year</div>
                  <div className="font-semibold text-gray-900">{car.year || 'N/A'}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-500 mb-1">Mileage</div>
                  <div className="font-semibold text-gray-900">{car.mileage ? `${car.mileage.toLocaleString()} km` : 'N/A'}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-500 mb-1">Transmission</div>
                  <div className="font-semibold text-gray-900">{car.transmission || 'N/A'}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-500 mb-1">Fuel Type</div>
                  <div className="font-semibold text-gray-900">{car.fuel_type || 'N/A'}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-500 mb-1">Make</div>
                  <div className="font-semibold text-gray-900">{car.make || 'N/A'}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-500 mb-1">Model</div>
                  <div className="font-semibold text-gray-900">{car.model || 'N/A'}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-500 mb-1">Dealer</div>
                  <div className="font-semibold text-gray-900">{car.dealer || car.source}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-500 mb-1">Location</div>
                  <div className="font-semibold text-gray-900">{car.location || 'National'}</div>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Before You Buy</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Always get an independent inspection before purchasing</li>
                    <li>• Verify the vehicle's service history and ownership</li>
                    <li>• Check for outstanding finance or stolen status</li>
                    <li>• Compare prices across multiple dealers</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-4">Estimated Costs</h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2 text-blue-600 font-semibold mb-1">
                    <Calculator className="w-4 h-4" />
                    Monthly Finance
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {monthlyFinance ? `R${monthlyFinance.toLocaleString()}/pm` : 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">72 months, 10% deposit, prime+2%</div>
                </div>

                <div className="p-4 bg-green-50 rounded-xl">
                  <div className="flex items-center gap-2 text-green-600 font-semibold mb-1">
                    <Shield className="w-4 h-4" />
                    Est. Insurance
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {monthlyInsurance ? `R${monthlyInsurance.toLocaleString()}/pm` : 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Comprehensive cover estimate</div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-2xl shadow-sm p-6 space-y-3">
              <button
                onClick={handleViewOriginal}
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
              >
                View on {car.source}
                <ExternalLink className="w-4 h-4" />
              </button>

              <button
                onClick={handleInspection}
                className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2"
              >
                <ClipboardCheck className="w-4 h-4" />
                Book Inspection
              </button>

              <button
                onClick={handleInsurance}
                className="w-full py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition flex items-center justify-center gap-2"
              >
                <Shield className="w-4 h-4" />
                Get Insurance Quote
              </button>

              <button
                onClick={handleFinance}
                className="w-full py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition flex items-center justify-center gap-2"
              >
                <Calculator className="w-4 h-4" />
                Calculate Finance
              </button>
            </div>

            {/* Source Info */}
            <div className="mt-6 p-4 bg-gray-100 rounded-xl">
              <p className="text-xs text-gray-500 text-center">
                This listing is from {car.source}. AutoSearchSA aggregates listings from multiple sources. 
                Always verify details with the seller.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarDetail;
